import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { adminApi } from '../../lib/http';
import { PortalButton, PortalCard, PortalEmpty, PortalInput, PortalTextarea } from '../../components/portal/PortalElements';

export default function AdminManualBooking() {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    customerName: '',
    email: '',
    phone: '',
    date: today,
    time: '11:00',
    pax: '2',
    specialRequest: '',
  });
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  async function loadBookings() {
    const response = await adminApi.get('/admin/bookings');
    setBookings(response.data || []);
  }

  useEffect(() => {
    loadBookings().catch((error) => toast.error(error.message || 'Unable to load manual bookings.'));
  }, []);

  const todayManualBookings = useMemo(
    () =>
      bookings
        .filter((booking) => booking.source === 'manual' && booking.date === today)
        .sort((left, right) => left.time.localeCompare(right.time)),
    [bookings, today],
  );

  async function submit() {
    setLoading(true);
    try {
      const response = await adminApi.post('/admin/bookings/manual', {
        ...form,
        pax: Number(form.pax),
      });
      toast.success(`Booking created: ${response.data.bookingId}`);
      setForm({
        customerName: '',
        email: '',
        phone: '',
        date: today,
        time: '11:00',
        pax: '2',
        specialRequest: '',
      });
      await loadBookings();
    } catch (error) {
      toast.error(error.message || 'Unable to create booking.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="portal-stack">
      <PortalCard>
        <div className="portal-section-head">
          <div>
            <p className="portal-kicker">Walk-in intake</p>
            <h2>Create manual booking</h2>
          </div>
        </div>
        <div className="portal-grid portal-grid-2">
          <PortalInput label="Guest name" value={form.customerName} onChange={(event) => setForm((current) => ({ ...current, customerName: event.target.value }))} />
          <PortalInput label="Email" type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
          <PortalInput label="Phone" value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} />
          <PortalInput label="Guests" type="number" min="1" value={form.pax} onChange={(event) => setForm((current) => ({ ...current, pax: event.target.value }))} />
          <PortalInput label="Date" type="date" value={form.date} onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))} />
          <PortalInput label="Time" type="time" value={form.time} onChange={(event) => setForm((current) => ({ ...current, time: event.target.value }))} />
        </div>
        <PortalTextarea
          label="Special request"
          rows="4"
          value={form.specialRequest}
          onChange={(event) => setForm((current) => ({ ...current, specialRequest: event.target.value }))}
        />
        <div className="portal-action-row">
          <PortalButton onClick={submit} disabled={loading}>
            {loading ? 'Creating...' : 'Create booking'}
          </PortalButton>
        </div>
      </PortalCard>

      <PortalCard>
        <div className="portal-section-head">
          <div>
            <p className="portal-kicker">Today</p>
            <h2>Manual bookings</h2>
          </div>
        </div>
        {todayManualBookings.length ? (
          <div className="portal-list">
            {todayManualBookings.map((booking) => (
              <div key={booking.bookingId} className="portal-list-item">
                <div>
                  <strong>{booking.customerName}</strong>
                  <p>{booking.bookingId} · {booking.time} · {booking.pax} guests</p>
                </div>
                <span className="portal-muted">{booking.status} · {booking.paymentStatus}</span>
              </div>
            ))}
          </div>
        ) : (
          <PortalEmpty title="No manual bookings yet" body="Create the first walk-in reservation for today." />
        )}
      </PortalCard>
    </div>
  );
}
